import React, {
  MouseEvent, useEffect, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components/macro';
import { css } from 'styled-components';
import Breakpoints from 'themes/constants/breakpoints';
import promoImg1 from 'assets/images/promo-1.png';
import promoImg2 from 'assets/images/promo-2.png';
import promoImg3 from 'assets/images/promo-3.png';

const buzzOut = keyframes`
  3% {
    transform: translateX(3px) rotate(2deg)
  }
  6% {
    transform: translateX(-3px) rotate(-2deg)
  }
  9% {
    transform: translateX(3px) rotate(2deg)
  }
  12% {
    transform: translateX(-3px) rotate(-2deg)
  }
  15% {
    transform: translateX(2px) rotate(1deg)
  }
  18% {
    transform: translateX(-2px) rotate(-1deg)
  }
  21% {
    transform: translateX(2px) rotate(1deg)
  }
  24% {
    transform: translateX(-2px) rotate(-1deg)
  }
  27% {
    transform: translateX(1px) rotate(0)
  }
  30% {
    transform: translateX(-1px) rotate(0)
  }
`;

const flash = keyframes`
  0% {
    left: -150%;
  }
  20% {
    left: 100%
  }
  100% {
    left: 150%
  }
`;

const Container = styled.div`
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
    box-sizing: border-box;
    padding: 20px 20px 0;
    -webkit-tap-highlight-color: transparent;
    @media only screen and (${Breakpoints.TABLET}) {
        max-width: 888px;
        padding: 0 24px 26px;
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        max-width: 1440px;
    }
`;

const Inner = styled.div<{ image?: string, highlight?: boolean }>`
    width: 100%;
    box-sizing: border-box;
    display: flex;
    position: relative;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
    overflow: hidden;
    height: 135px;
    border-radius: 12px;
    background-image: ${({ image }) => `url("${image}")`};
    background-size: cover;
    background-repeat: no-repeat;
    background-position: 50% 50%;
    cursor: pointer;
    padding: 20px;
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        padding: 30px 40px;
    }
    ${({ highlight }) => highlight && css`
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      transform: translateY(-50%) skewX(-30deg);
      display: block;
      height: 100%;
      width: 100%;
      background-image: linear-gradient(90deg, rgba(0,0,0,0), hsla(0,0%,100%,.3) 90%, rgba(0,0,0,0));
      animation: ${flash} 5s ease-in-out infinite;
      z-index: 100;
      padding: 0 10px;
    }
  `}
`;

const InnerType1 = styled(Inner)`
    background-color: #fff;
    flex-direction: column;
    align-items: flex-start;
    background-size: 150%;
    background-position: -5% 50%;
    @media only screen and (${Breakpoints.TABLET}) {
        flex-direction: row;
        align-items: center;
        background-position: 50% 58%;
    }
`;

const InnerType2 = styled(Inner)`
    flex-direction: column;
    align-items: flex-start;
    background-image: linear-gradient(0deg, rgba(0, 0, 0, 0.301699) 0%, 
      rgba(255, 255, 255, 0) 50.03%, rgba(0, 0, 0, 0.5) 100%), url(${promoImg2});
    background-size: 140%;
    background-position: 5% 60%;
    @media only screen and (${Breakpoints.TABLET}) {
        background-size: cover;
        background-position: 50% 35%;
        flex-direction: row;
        align-items: center;
    }
    @media (min-width: 960px) {
        background-position: 50% 40%;
        flex-direction: row;
        align-items: center;
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        background-image: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, 
          rgba(0, 0, 0, 0.391699) 60.92%, rgba(0, 0, 0, 0.63) 100%), url(${promoImg2});
    }
`;

const InnerType3 = styled(Inner)`
    @media only screen and (${Breakpoints.TABLET}) {
        background-position: 50% 100%;
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        background-position: 50% 80%;
    }
`;

const Text = styled.span`
    font-size: 14px;
    font-weight: 700;
    font-family: Roboto, sans-serif;
    color: #fff;
    text-transform: uppercase;
    width: 135px;
    @media (min-width: 360px) {
        font-size: 20px;
        width: 160px;
    }
    @media only screen and (${Breakpoints.TABLET}) {
        font-size: 30px;
        width: 260px;
    }
    @media (min-width: 960px) {
        left: 0;
        width: 500px;
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        font-size: 40px;
        width: 700px;
    }
`;

const TextType1 = styled(Text)`
    text-align: center;
    @media only screen and (${Breakpoints.TABLET}) {
        width: 220px;
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        width: 300px;
        line-height: 40px;
    }
`;

const TextType2 = styled(Text)`
    width: 90px;
    @media only screen and (${Breakpoints.TABLET}) {
        width: 500px;
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        display: flex;
        justify-content: center;
    }
`;

const TextType3 = styled(Text)`
    @media only screen and (${Breakpoints.TABLET}) {
        text-align: right;
        position: relative;
        top: -20px;
    }
    @media (min-width: 960px) {
        top: 0;
    }
`;

const TextType4 = styled(Text)<{ rightAlign?: boolean }>`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    text-align: center;
    width: ${({ rightAlign }) => (rightAlign ? '104px' : '145px')};
    font-size: 24px;
    @media only screen and (max-width: 340px) {
        font-size: 20px;
    }
    @media only screen and (${Breakpoints.TABLET}) {
        font-size: 33px;
        width: ${({ rightAlign }) => (rightAlign ? '300px' : '385px')};
        flex-direction: row;
        justify-content: ${({ rightAlign }) => (rightAlign ? 'flex-end' : 'flex-start')};
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        font-size: 47px;
        width: 550px;
    }
`;

const TextLink = styled.span`
    text-align: center;
    bottom: 20px;
    text-transform: uppercase;
    text-decoration: underline;
    font-weight: 400;
    animation: ${buzzOut} 2.7s infinite;
    color: #fff;
    font-size: 15px;
    @media (min-width: 1280px) {
        background: transparent;
        font-weight: 700;
        font-size: 20px;
    }
`;

const TextLinkRightSide = styled(TextLink)`
    position: absolute;
    right: 20px;
    bottom: 20px;
    background: rgba(0, 0, 0, 0.6);
    padding: 8px;
    border-radius: 6px;
    @media only screen and (${Breakpoints.TABLET}) {
        right: 20px;
        bottom: 10px;
    }
    @media only screen and (${Breakpoints.LAPTOP_L}) {
        background: transparent;
        font-weight: 700;
        padding: 0;
        right: 40px;
        bottom: 15px;
        font-size: 20px;
    }
`;

const TextLinkWrap = styled.div`
    animation: ${buzzOut} 2.7s infinite;
`;

function PromoBanner() {
  const [randomItem, setRandomItem] = useState<JSX.Element | null>(null);
  const { pathname } = useLocation();

  const banner1 = (
    <InnerType3 image={promoImg1} highlight>
      <TextType4>
        <span style={{ fontWeight: 700 }}>
          ешь
          &nbsp;
        </span>
        <span style={{ fontWeight: 400 }}>
          двигайся
          &nbsp;
        </span>
        <span style={{ fontStyle: 'italic' }}>
          спи
          &nbsp;
        </span>
      </TextType4>
      <TextType4 rightAlign>
        читать тут
      </TextType4>
    </InnerType3>
  );

  const banner2 = (
    <InnerType2>
      <TextType2>
        готовь и читай
      </TextType2>
      <TextType3>
        только бестселлеры
      </TextType3>
      <TextLinkRightSide>
        открыть
      </TextLinkRightSide>
    </InnerType2>
  );

  const banner3 = (
    <InnerType1 image={promoImg3}>
      <TextType1 style={{ color: 'black' }}>
        читать тут
        <TextLinkWrap>
          <TextLink style={{ color: 'black' }}>
            перейти
          </TextLink>
        </TextLinkWrap>
      </TextType1>
      <TextType1 style={{ color: 'black' }}>
        только бестселлеры
      </TextType1>
    </InnerType1>
  );

  const items = [banner1, banner2, banner3];

  useEffect(() => {
    const getRandomItem = () => {
      const shuffledItems = [...items].sort(() => 0.5 - Math.random());
      setRandomItem(shuffledItems[0]);
    };

    getRandomItem();
  }, [pathname]);

  const link = 'http://lk10.life.by/lp/flow/l_premium_smartbooks';

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    window.location.replace(link);
  };

  return (
    <Container>
      <div onClick={handleClick} aria-hidden="true">
        {randomItem}
      </div>
    </Container>
  );
}

export default PromoBanner;
